// src/pages/VerifyEmail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

function stripHtml(s) {
  if (!s) return "";
  return String(s)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function VerifyEmail() {
  const nav = useNavigate();

  // ✅ NEW short-link format: /v/:token
  const params = useParams();
  const tokenFromPath = params?.token || "";

  // ✅ Backward-compat: /verify?token=...
  const [sp] = useSearchParams();
  const tokenFromQuery = sp.get("token") || "";

  const token = useMemo(() => {
    return tokenFromPath || tokenFromQuery || "";
  }, [tokenFromPath, tokenFromQuery]);

  const [status, setStatus] = useState("pending"); // pending | ok | error
  const [msg, setMsg] = useState("Vérification en cours…");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!token) {
        setStatus("error");
        setMsg("Lien invalide : aucun jeton fourni.");
        return;
      }

      try {
        // Backend endpoint stays the same:
        // GET /api/auth/verify-email?token=...
        const url = `/api/auth/verify-email?token=${encodeURIComponent(token)}`;

        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          redirect: "follow",
        });

        if (cancelled) return;

        // Backend usually redirects to /compte?verified=1
        // fetch() follows redirects; we detect it and navigate cleanly.
        if (res.redirected && res.url) {
          try {
            const dest = new URL(res.url);
            if (dest.origin === window.location.origin) {
              nav(dest.pathname + dest.search, { replace: true });
              return;
            }
            // If it ever becomes cross-origin (unlikely here), hard redirect
            window.location.assign(res.url);
            return;
          } catch (e) {
            // fallback
            nav("/compte?verified=1", { replace: true });
            return;
          }
        }

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          setStatus("error");
          setMsg(stripHtml(text) || "Échec de vérification.");
          return;
        }

        setStatus("ok");
        setMsg("Adresse e-mail vérifiée !");
        setTimeout(() => {
          nav("/compte?verified=1", { replace: true });
        }, 700);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMsg("Impossible de contacter le serveur.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, nav]);

  return (
    <div className="results-page" style={{ minHeight: "100vh" }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Vérification e-mail — État du Lieu</title>
      </Helmet>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 16px" }}>
        <div className="glass-chip" style={{ marginBottom: 12 }}>
          <Link to="/">← Retour</Link>
        </div>

        <div
          className="card"
          style={{
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 18,
            padding: 20,
          }}
        >
          <h2 style={{ marginTop: 0 }}>Vérification de l’e-mail</h2>

          <p style={{ marginBottom: 10 }}>{msg}</p>

          {status === "pending" && (
            <p style={{ fontSize: 13, color: "#64748b" }}>
              Merci de patienter… (redirection automatique)
            </p>
          )}

          {status === "ok" && (
            <p>
              Redirection vers <Link to="/compte">Mon compte</Link>…
            </p>
          )}

          {status === "error" && (
            <div style={{ marginTop: 12 }}>
              <Link className="download-link" to="/compte">
                Aller à Mon compte
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
